import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Button, Icon, Skeleton, Tag, Typography } from '@ann/web-components';
import { ASSIGNEE_TYPES, SHARE_MODAL_COPY } from '../../../../constants';
import { getAvatarSrc, getInitials } from '../../../../utils/avatarHelpers';
import {
  AvatarWrap,
  Container,
  DepartmentSectionHeader,
  DropdownItemButton,
  DropdownItemText,
  DropdownList,
  DropdownPanel,
  DropdownScrollbar,
  DropdownScrollInner,
  ResultsBlock,
  EmptyState,
  EmptyStateText,
  InputArea,
  LoadingBlock,
  NativeInput,
  SearchFieldWrap,
  SearchIconWrap,
  TagsAndInputInner,
  TagsInputScrollbar,
  TagsScrollOuter,
} from './UserDepartmentSearch.styled';

const DEPT_HEADER_STYLE = { letterSpacing: '0.48px', textTransform: 'uppercase' };

const SKELETON_ITEMS = Array.from({ length: 6 }, (_, i) => (
  <Skeleton key={`sk-${i}`} height="40px" borderRadius="8px" animation />
));

const UserDepartmentSearch = ({
  searchTerm = '',
  onSearchChange,
  searchResults = { users: [], departments: [] },
  searchLoading = false,
  onSelect,
  assignments = [],
  onRemove,
  onAdd,
  showAssignmentTags = true,
}) => {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false);

  const assignedIds = useMemo(
    () => new Set(assignments.map((a) => a.id)),
    [assignments]
  );

  const filteredUsers = useMemo(
    () => (searchResults?.users || []).filter((u) => !assignedIds.has(u.id)),
    [searchResults?.users, assignedIds]
  );

  const filteredDepartments = useMemo(
    () => (searchResults?.departments || []).filter((d) => !assignedIds.has(d.id)),
    [searchResults?.departments, assignedIds]
  );

  const trimmedSearch = searchTerm.trim();
  const hasListItems = filteredUsers.length > 0 || filteredDepartments.length > 0;
  const showPanelOpen =
    inputFocused && (trimmedSearch.length > 0 || searchLoading || hasListItems);

  const showEmpty =
    showPanelOpen &&
    !searchLoading &&
    trimmedSearch.length > 0 &&
    !hasListItems;

  const hasAssignments = assignments.length > 0;
  const hasInlineTags = showAssignmentTags && hasAssignments;
  const hasDepartmentRows = filteredDepartments.length > 0;

  const handleSearchChange = useCallback(
    (e) => {
      onSearchChange?.(e.target.value);
    },
    [onSearchChange]
  );

  const handleInputFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      setInputFocused(false);
      blurTimeoutRef.current = null;
    }, 180);
  }, []);

  const handleDropdownMouseDown = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handlePick = useCallback(
    (item) => {
      if (item?.id) {
        onSelect?.(item);
      }
      onSearchChange?.('');
      inputRef.current?.focus();
    },
    [onSearchChange, onSelect]
  );

  const handleInputShellMouseDown = useCallback((e) => {
    if (inputRef.current?.contains(e.target)) return;
    if (e.target.closest('button')) return;
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showPanelOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setInputFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, { passive: true });
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showPanelOpen]);

  const inputIsActive = inputFocused || showPanelOpen;

  return (
    <Container>
      <SearchFieldWrap ref={rootRef}>
        <InputArea
          $isActive={inputIsActive}
          $compact={!hasInlineTags}
          onMouseDown={handleInputShellMouseDown}
          role="presentation"
        >
          <SearchIconWrap aria-hidden $expandable={hasInlineTags}>
            <Icon name="search" size="small" color="neutral" />
          </SearchIconWrap>
          <TagsScrollOuter $expandable={hasInlineTags}>
            <TagsInputScrollbar $expandable={hasInlineTags}>
              <TagsAndInputInner $compact={!hasInlineTags}>
                {showAssignmentTags &&
                  assignments.map((item) => {
                    const isUser = item?.type === ASSIGNEE_TYPES.USER;
                    const tagColor = isUser ? 'informative' : 'success';
                    const tooltipText = isUser
                      ? SHARE_MODAL_COPY.tooltips.userRole(item.role)
                      : SHARE_MODAL_COPY.tooltips.departmentMembers(item.userCount ?? 0);
                    return (
                      <Tag
                        key={item.id}
                        closable
                        color={tagColor}
                        size="small"
                        text={item.name}
                        tooltip={tooltipText}
                        onClose={() => onRemove?.(item.id)}
                      />
                    );
                  })}
                <NativeInput
                  ref={inputRef}
                  type="text"
                  autoComplete="off"
                  placeholder={SHARE_MODAL_COPY.searchPlaceholder}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  aria-expanded={showPanelOpen}
                  aria-controls="share-dashboard-search-listbox"
                  aria-autocomplete="list"
                  role="combobox"
                />
              </TagsAndInputInner>
            </TagsInputScrollbar>
          </TagsScrollOuter>
        </InputArea>

        <DropdownPanel
          id="share-dashboard-search-listbox"
          role="listbox"
          $isOpen={showPanelOpen}
          onMouseDown={handleDropdownMouseDown}
        >
          <DropdownScrollInner>
            <DropdownScrollbar>
              <DropdownList>
                <LoadingBlock $visible={searchLoading}>
                  {SKELETON_ITEMS}
                </LoadingBlock>

                <EmptyState $visible={showEmpty}>
                  <EmptyStateText>{SHARE_MODAL_COPY.noResults}</EmptyStateText>
                </EmptyState>

                <ResultsBlock $visible={!searchLoading && !showEmpty}>
                  {filteredUsers.map((user) => (
                    <DropdownItemButton
                      key={user.id}
                      type="button"
                      role="option"
                      onClick={() => handlePick(user)}
                    >
                      <AvatarWrap>
                        <Avatar
                          width={24}
                          height={24}
                          alt=""
                          label={getInitials(user.name)}
                          src={getAvatarSrc(user.name, user.id)}
                        />
                      </AvatarWrap>
                      <DropdownItemText>
                        <Typography category="body" size="medium" weight="medium" color="body">
                          {user.role
                            ? `${user.name} · ${user.role}`
                            : user.name}
                        </Typography>
                      </DropdownItemText>
                    </DropdownItemButton>
                  ))}

                  <DepartmentSectionHeader $visible={hasDepartmentRows}>
                    <Typography
                      category="caption"
                      size="small"
                      weight="bold"
                      color="subtitle"
                      style={DEPT_HEADER_STYLE}
                    >
                      {SHARE_MODAL_COPY.sections.departments}
                    </Typography>
                  </DepartmentSectionHeader>

                  {filteredDepartments.map((dept) => (
                    <DropdownItemButton
                      key={dept.id}
                      type="button"
                      role="option"
                      onClick={() => handlePick(dept)}
                    >
                      <AvatarWrap>
                        <Avatar
                          width={24}
                          height={24}
                          alt=""
                          label={getInitials(dept.name)}
                          src={getAvatarSrc(dept.name, dept.id)}
                        />
                      </AvatarWrap>
                      <DropdownItemText>
                        <Typography category="body" size="medium" weight="medium" color="body">
                          {dept.name}
                        </Typography>
                      </DropdownItemText>
                    </DropdownItemButton>
                  ))}
                </ResultsBlock>
              </DropdownList>
            </DropdownScrollbar>
          </DropdownScrollInner>
        </DropdownPanel>
      </SearchFieldWrap>
      <Button
        label={SHARE_MODAL_COPY.addButton}
        type="primary"
        disabled={!hasAssignments}
        size="large"
        className="add-button"
        onClick={() => onAdd?.()}
      />
    </Container>
  );
};

export default memo(UserDepartmentSearch);
